import { motion } from 'framer-motion';

const float = (delay = 0, y = 14) => ({
  animate: { y: [0, -y, 0], rotate: [0, 4, 0] },
  transition: { duration: 6 + delay, repeat: Infinity, ease: 'easeInOut', delay },
});

export default function Hero() {
  return (
    <section id="start" className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 overflow-hidden grain">
      {/* Background blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 size-[520px] rounded-full bg-sun/60 blur-3xl" />
        <div className="absolute top-40 -right-40 size-[560px] rounded-full bg-sky/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 size-[420px] rounded-full bg-bubble/40 blur-3xl" />
      </div>

      {/* Floating decorative shapes */}
      <motion.div {...float(0)} className="hidden md:block absolute top-32 left-[6%] size-16 rounded-2xl bg-tomato shadow-pop rotate-12" aria-hidden />
      <motion.div {...float(1.2, 18)} className="hidden md:block absolute top-48 right-[8%] size-12 rounded-full bg-grass shadow-pop" aria-hidden />
      <motion.div {...float(2, 10)} className="hidden md:block absolute bottom-24 left-[10%] size-14 rounded-full bg-grape shadow-pop" aria-hidden />
      <motion.div {...float(0.6, 22)} className="hidden md:block absolute bottom-32 right-[14%] size-20 rounded-[2rem] bg-sun shadow-pop -rotate-6" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <h1
              className="mt-5 font-display text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-[5.2rem] lg:leading-[0.95] font-semibold tracking-tight text-balance"
            >
              Hinderbanor
              <span className="relative inline-block">
                <span className="relative z-10 text-tomato">Hoppborgar</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden>
                  <path d="M2 9 Q 50 2, 100 7 T 198 6" fill="none" stroke="currentColor" className="text-sun" strokeWidth="5" strokeLinecap="round" />
                </svg>
              </span>{' '}
              & aktiviteter <span className="text-sky">för alla</span> tillfällen!
            </h1>

            <p
              className="mt-6 text-lg sm:text-xl text-ink/70 max-w-xl mx-auto lg:mx-0 text-pretty"
            >
              Uthyrning, försäljning och event i hela Sverige. Vi gör festen, kalaset och företagseventet till en oförglömlig studsande upplevelse — sedan 2002.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <a
                href="#aktiviteter"
                className="group relative inline-flex items-center justify-center gap-2 rounded-full bg-tomato text-cream px-7 py-4 text-base font-semibold shadow-bouncy transition-transform hover:-translate-y-0.5 active:translate-y-0"
              >
                Se våra aktiviteter
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-cream border-2 border-ink px-7 py-4 text-base font-semibold hover:bg-sun transition-colors"
              >
                Kontakta oss
              </a>
            </div>

            <div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 justify-center lg:justify-start text-sm text-ink/60"
            >
              <Stat value="20+" label="Års erfarenhet" />
              <Divider />
              <Stat value="600+" label="Modeller" />
              <Divider />
              <Stat value="10 000+" label="Glada gäster" />
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 relative">
            <div
              className="relative mx-auto max-w-md"
            >
              <BounceCastle />

              {/* Orbiting badges */}
              <motion.div
                {...float(0.4, 12)}
                className="absolute -top-4 -left-6 sm:-left-10 rounded-2xl bg-cream border-2 border-ink shadow-pop px-4 py-3"
              >
                <div className="text-2xl">🎉</div>
                <div className="text-xs font-semibold mt-0.5">Levereras hem</div>
              </motion.div>
              <motion.div
                {...float(1.4, 16)}
                className="absolute -bottom-2 -right-2 sm:-right-6 rounded-2xl bg-grass text-cream shadow-pop px-4 py-3 rotate-3"
              >
                <div className="text-xs font-semibold">från</div>
                <div className="text-2xl font-display font-bold leading-none">1500 kr</div>
                <div className="text-[10px] mt-0.5 opacity-90">/ dag</div>
              </motion.div>
              <motion.div
                {...float(0.9, 10)}
                className="absolute top-1/3 -right-8 sm:-right-12 rounded-full bg-sun border-2 border-ink shadow-pop size-16 grid place-items-center -rotate-6"
              >
                <span className="text-2xl">🏰</span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2 text-xs text-ink/50"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="inline-block"
        >
          ↓
        </motion.span>
        Bläddra
      </div>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-left">
      <div className="font-display text-2xl font-bold text-ink leading-none">{value}</div>
      <div className="mt-1">{label}</div>
    </div>
  );
}

function Divider() {
  return <span className="hidden sm:inline-block h-8 w-px bg-ink/15" />;
}

function BounceCastle() {
  return (
    <svg viewBox="0 0 360 360" className="w-full h-auto drop-shadow-[0_30px_40px_rgba(15,27,45,0.25)]" aria-hidden>
      {/* Ground shadow */}
      <ellipse cx="180" cy="330" rx="140" ry="14" fill="#0F1B2D" opacity="0.12" />

      {/* Main castle body */}
      <g>
        {/* Base bouncy floor */}
        <rect x="40" y="220" width="280" height="80" rx="22" fill="#FF4D5E" />
        <rect x="40" y="220" width="280" height="14" rx="7" fill="#fff" opacity="0.18" />

        {/* Left tower */}
        <rect x="55" y="120" width="70" height="120" rx="18" fill="#2EA8E0" />
        <path d="M55 130 Q 90 95, 125 130 Z" fill="#FFD23F" />
        <circle cx="90" cy="105" r="6" fill="#FF4D5E" />
        <rect x="68" y="148" width="44" height="40" rx="10" fill="#0F1B2D" opacity="0.22" />
        <rect x="74" y="154" width="32" height="28" rx="6" fill="#FFF8EC" />

        {/* Right tower */}
        <rect x="235" y="120" width="70" height="120" rx="18" fill="#34C77B" />
        <path d="M235 130 Q 270 95, 305 130 Z" fill="#FFD23F" />
        <circle cx="270" cy="105" r="6" fill="#FF4D5E" />
        <rect x="248" y="148" width="44" height="40" rx="10" fill="#0F1B2D" opacity="0.22" />
        <rect x="254" y="154" width="32" height="28" rx="6" fill="#FFF8EC" />

        {/* Center arch */}
        <path d="M130 240 L130 175 Q180 130, 230 175 L230 240 Z" fill="#7C5CFF" />
        <path d="M145 240 L145 185 Q180 152, 215 185 L215 240 Z" fill="#0F1B2D" opacity="0.85" />
        <path d="M150 240 L150 188 Q180 158, 210 188 L210 240 Z" fill="#FFF8EC" />

        {/* Stripes on base */}
        <g fill="#FFD23F">
          <rect x="60" y="260" width="14" height="32" rx="4" />
          <rect x="100" y="260" width="14" height="32" rx="4" />
          <rect x="140" y="260" width="14" height="32" rx="4" />
          <rect x="180" y="260" width="14" height="32" rx="4" />
          <rect x="220" y="260" width="14" height="32" rx="4" />
          <rect x="260" y="260" width="14" height="32" rx="4" />
          <rect x="290" y="260" width="14" height="32" rx="4" />
        </g>

        {/* Top flags */}
        <g>
          <line x1="90" y1="100" x2="90" y2="70" stroke="#0F1B2D" strokeWidth="3" strokeLinecap="round" />
          <path d="M90 70 L 112 76 L 90 84 Z" fill="#FF4D5E" />
          <line x1="270" y1="100" x2="270" y2="70" stroke="#0F1B2D" strokeWidth="3" strokeLinecap="round" />
          <path d="M270 70 L 292 76 L 270 84 Z" fill="#7C5CFF" />
          <line x1="180" y1="155" x2="180" y2="120" stroke="#0F1B2D" strokeWidth="3" strokeLinecap="round" />
          <path d="M180 120 L 206 126 L 180 134 Z" fill="#FFD23F" />
        </g>
      </g>
    </svg>
  );
}
